import { useEffect, useId, useRef, useState, type HTMLAttributes } from 'react';

type Props = HTMLAttributes<HTMLOrSVGElement> & {
    density?: number,
    size?: number,
    width?: number,
    height?: number,
    color?: string,
    className?: string
}

export default function FillDots({
    density = 10,
    size = 4,
    color = 'black',
    width = 512,
    height = 512,
    className,
    ...rest
}: Props) {
    const componentId = useId();
    const rootRef = useRef<SVGSVGElement | null>(null);

    const [columns, setColumns] = useState(density);
    const [rows, setRows] = useState(density);
    const [columnSpacing, setColumnSpacing] = useState(0);
    const [rowSpacing, setRowSpacing] = useState(0);

    useEffect(() => {
        if (!rootRef.current) return;

        const { width: w, height: h } = rootRef.current.getBoundingClientRect();
        const aspectRatio = w / h;

        const adjustedColumns = Math.floor(density * aspectRatio);
        const adjustedRows = Math.floor(density);

        setColumns(adjustedColumns);
        setRows(adjustedRows);
        setColumnSpacing(w / adjustedColumns);
        setRowSpacing(h / adjustedRows);
    }, [density, width, height]);

    return (
        <svg
            ref={rootRef}
            className={className}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            fill="none"
            {...rest}
        >
            {Array.from({ length: rows }, (_, y) =>
                Array.from({ length: columns }, (_, x) => (
                    <circle
                        key={`${componentId}_${y}_${x}`}
                        cx={x * columnSpacing + columnSpacing / 2}
                        cy={y * rowSpacing}
                        r={size / 2}
                        fill={color}
                    />
                ))
            )}
        </svg>
    );
}

